import SEO from "@/components/SEO";
import { SeoHead } from '@/components/SeoHead';
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Smartphone, Dock, Tablet, Zap, Code } from "lucide-react";

export default function CustomApps() {
  const { openDrawer } = useQuoteDrawer();

  const appTypes = [
    {
      title: "Mobile Applications",
      description: "Native and cross-platform mobile apps for iOS and Android with exceptional user experiences.",
      icon: <Smartphone className="h-8 w-8" />,
      features: ["Native iOS & Android", "Cross-platform", "Push Notifications", "Offline Functionality"]
    },
    {
      title: "Dock Applications",
      description: "Powerful desktop applications for Windows, macOS, and Linux with rich user interfaces.",
      icon: <Dock className="h-8 w-8" />,
      features: ["Cross-platform Dock", "Rich UI/UX", "System Integration", "High Performance"]
    },
    {
      title: "Web Applications",
      description: "Modern web applications with responsive design and progressive web app capabilities.",
      icon: <Tablet className="h-8 w-8" />,
      features: ["Progressive Web Apps", "Responsive Design", "Real-time Updates", "Cloud Integration"]
    },
    {
      title: "Enterprise Solutions",
      description: "Custom enterprise applications tailored to specific business processes and workflows.",
      icon: <Zap className="h-8 w-8" />,
      features: ["Business Process Automation", "Integration APIs", "Scalable Architecture", "Security Focused"]
    }
  ];

  const technologies = [
    { category: "Mobile", tools: ["React Native", "Flutter", "Swift", "Kotlin"] },
    { category: "Dock", tools: ["Electron", "Qt", "WPF", ".NET MAUI"] },
    { category: "Web", tools: ["React", "Vue.js", "Angular", "Node.js"] },
    { category: "Backend", tools: ["Python", "Java", "C#", "PostgreSQL"] }
  ];

  const industries = [
    { name: "Healthcare", icon: "fas fa-heartbeat", description: "Medical and healthcare applications" },
    { name: "Finance", icon: "fas fa-university", description: "Financial and banking solutions" },
    { name: "Education", icon: "fas fa-graduation-cap", description: "Educational and e-learning apps" },
    { name: "Retail", icon: "fas fa-store", description: "Retail and e-commerce applications" },
    { name: "Manufacturing", icon: "fas fa-industry", description: "Industrial and manufacturing apps" },
    { name: "Logistics", icon: "fas fa-shipping-fast", description: "Supply chain and logistics solutions" }
  ];

  return (
    <>
      <SeoHead 
        title="Custom Software Development UAE | ZeeX Soft"
        description="Tailored business software solutions in UAE. Build scalable, secure custom applications for your unique needs since 2017."
        keywords="custom software uae, business application development, enterprise solutions dubai"
      />

      <main>
    

     
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[url('/images/dot-pattern.svg')] bg-[size:40px_40px] opacity-5"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900/80"></div>
          </div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="lg:w-1/2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <motion.span 
                    className="inline-block px-4 py-2 text-sm font-medium rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white mb-6 shadow-lg"
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    Custom App Development
                  </motion.span>
                  <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                    Tailored <span className="bg-gradient-to-r from-blue-400 to-indigo-500 bg-clip-text text-transparent">Software Solutions</span> For Your Business
                  </h1>
                  <p className="text-lg text-gray-300 mb-8 max-w-lg">
                    We build custom applications that streamline operations, enhance productivity, and drive growth for your unique business needs.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                      <Button 
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg"
                        onClick={openDrawer}
                      >
                        Get a Free Consultation
                      </Button>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                      <Link href="/portfolio">
                        <Button variant="outline" className="text-white border-white/50 hover:border-white hover:bg-white/10 backdrop-blur-sm">
                          View Case Studies
                        </Button>
                      </Link>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
              
              <div className="lg:w-1/2">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="relative"
                >
                  <div className="absolute -top-10 -left-10 w-64 h-64 bg-blue-500/10 rounded-full filter blur-3xl animate-pulse"></div>
                  <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-indigo-500/10 rounded-full filter blur-3xl animate-pulse"></div>
                  
                  <div className="relative bg-gray-800/50 backdrop-blur-md border border-gray-700/50 rounded-2xl p-8 shadow-2xl overflow-hidden">
                    <div className="absolute -right-20 -top-20 w-64 h-64 bg-blue-500/5 rounded-full filter blur-xl"></div>
                    <div className="flex justify-center mb-8">
                      <Code className="h-20 w-20 text-blue-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-white text-center mb-4">Custom Solutions</h3>
                    <p className="text-gray-300 text-center max-w-md mx-auto">
                      Designed specifically for your workflow and business requirements with cutting-edge technology
                    </p>
                    <div className="mt-8 flex justify-center">
                      <div className="flex space-x-2">
                        {['React', 'Node.js', 'Python', 'Flutter'].map((tech, i) => (
                          <span key={i} className="px-3 py-1 text-xs font-medium rounded-full bg-gray-700/50 text-gray-300">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* App Types Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Custom Application Types</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                We develop custom applications across all platforms and devices
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {appTypes.map((app, index) => (
                <motion.div
                  key={app.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-effect p-8 rounded-xl"
                >
                  <div className="w-16 h-16 bg-secondary/20 rounded-lg flex items-center justify-center mb-6 text-secondary">
                    {app.icon}
                  </div>
                  <h3 className="font-orbitron font-semibold text-xl mb-4">{app.title}</h3>
                  <p className="text-muted-foreground mb-6">{app.description}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {app.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-secondary rounded-full flex items-center justify-center">
                          <span className="text-primary-foreground text-xs">✓</span>
                        </div>
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies Section */}
        <section className="py-20 bg-card">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Development Technologies</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                We use the latest technologies and frameworks for custom app development
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {technologies.map((tech, index) => (
                <motion.div
                  key={tech.category}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-effect p-6 rounded-xl"
                >
                  <h3 className="font-orbitron font-semibold text-lg mb-4 text-secondary">{tech.category}</h3>
                  <div className="space-y-2">
                    {tech.tools.map((tool, toolIndex) => (
                      <div key={toolIndex} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-secondary rounded-full"></div>
                        <span className="text-muted-foreground text-sm">{tool}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Industries We Serve</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Custom applications for various industries and business sectors
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {industries.map((industry, index) => (
                <motion.div
                  key={industry.name}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-effect p-6 rounded-xl text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-secondary to-accent rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${industry.icon} text-xl text-primary-foreground`}></i>
                  </div>
                  <h3 className="font-orbitron font-semibold text-lg mb-3">{industry.name}</h3>
                  <p className="text-muted-foreground text-sm">{industry.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Development Process */}
        <section className="py-20 bg-card">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Development Process</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Agile development methodology for efficient and quality app delivery
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
              {[
                { step: "01", title: "Discovery", description: "Requirements analysis and project planning" },
                { step: "02", title: "Design", description: "UI/UX design and user experience planning" },
                { step: "03", title: "Development", description: "Agile development with regular updates" },
                { step: "04", title: "Testing", description: "Comprehensive testing and quality assurance" },
                { step: "05", title: "Deployment", description: "App deployment and ongoing support" }
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-secondary to-accent rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="font-orbitron font-bold text-primary-foreground">{item.step}</span>
                  </div>
                  <h3 className="font-orbitron font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Project Examples */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Recent Projects</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Examples of custom applications we've developed
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "Healthcare Management App",
                  description: "Custom mobile app for patient management and telemedicine",
                  platform: "Mobile App",
                  tech: ["React Native", "Node.js", "MongoDB"]
                },
                {
                  title: "Financial Dashboard",
                  description: "Dock application for financial reporting and analytics",
                  platform: "Dock App",
                  tech: ["Electron", "React", "PostgreSQL"]
                },
                {
                  title: "Inventory Management System",
                  description: "Web-based inventory tracking and management solution",
                  platform: "Web App",
                  tech: ["Vue.js", "Python", "Redis"]
                }
              ].map((project, index) => (
                <motion.div
                  key={project.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-effect rounded-xl overflow-hidden"
                >
                  <div className="h-48 bg-gradient-to-r from-secondary/20 to-accent/20 flex items-center justify-center">
                    <span className="text-muted-foreground">Project Showcase</span>
                  </div>
                  <div className="p-6">
                    <div className="mb-2">
                      <span className="px-2 py-1 bg-secondary/20 text-secondary text-xs rounded">{project.platform}</span>
                    </div>
                    <h3 className="font-orbitron font-semibold text-lg mb-2">{project.title}</h3>
                    <p className="text-muted-foreground text-sm mb-4">{project.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {project.tech.map((tech, techIndex) => (
                        <span key={techIndex} className="px-2 py-1 bg-accent/20 text-accent text-xs rounded">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-secondary/10 to-accent/10">
          <div className="container mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">
                Ready to Build Your Custom App?
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Transform your ideas into powerful custom applications that drive business growth and efficiency
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={openDrawer} className="btn-primary">
                  Start Your Project
                </Button>
                <Link href="/contact">
                  <Button className="btn-secondary">
                    Discuss Your Idea
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
